import React from 'react';
import { Utensils, MapPin, Clock, CheckCircle, XCircle, Edit } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import type { NotificationItem } from '@/hooks/useNotifications';

interface ProposalResultNotificationItemProps {
  notification: NotificationItem;
  isUnread: boolean;
  onClose: () => void;
}

export function ProposalResultNotificationItem({ notification, isUnread, onClose }: ProposalResultNotificationItemProps) {
  const navigate = useNavigate();
  const { data } = notification;

  const handleViewEntity = () => {
    if (data.entity_type === 'burger' && data.entity_id) {
      navigate(`/burgers/${data.entity_id}`);
    } else if (data.entity_type === 'producer' && data.entity_id) {
      navigate(`/producers/${data.entity_id}`);
    }
    onClose();
  };

  const isAccepted = data.status === 'accepted';
  const isBurger = data.entity_type === 'burger';
  const EntityIcon = isBurger ? Utensils : MapPin;
  
  const StatusIcon = isAccepted ? CheckCircle : XCircle;
  const statusBgClass = isAccepted 
    ? "bg-green-100 dark:bg-green-900/20" 
    : "bg-red-100 dark:bg-red-900/20";
  const statusTextClass = isAccepted 
    ? "text-green-600 dark:text-green-400" 
    : "text-red-600 dark:text-red-400";

  const getMessage = () => {
    if (isAccepted) {
      if (data.was_edited) {
        return `Your description for "${data.entity_name}" was accepted with edits`;
      }
      return `Your description for "${data.entity_name}" was accepted`;
    }
    return `Your description for "${data.entity_name}" was declined`;
  };

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer",
        isUnread && "bg-primary/5 border-l-2 border-primary"
      )}
      onClick={handleViewEntity}
    >
      <div className={cn("h-12 w-12 rounded-full flex items-center justify-center", statusBgClass)}>
        <StatusIcon className={cn("h-6 w-6", statusTextClass)} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <div className="flex items-center gap-1.5">
            <EntityIcon className="h-4 w-4 text-muted-foreground" />
            <p className={cn(
              "text-foreground text-sm",
              isUnread ? "font-semibold" : "font-medium"
            )}>
              {getMessage()}
            </p>
          </div>
        </div>
        
        {isAccepted && data.was_edited && (
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-1">
            <Edit className="h-3 w-3" />
            <span>The owner made some changes to your suggestion</span>
          </div>
        )}
        
        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-2">
          <Clock className="h-3 w-3" />
          <span>
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>
    </div>
  );
}
